class SoundNotificationService {
    constructor() {
        this.audioContext = null;
        this.isEnabled = true;
        this.ringtoneEnabled = true;
        this.initAudioContext();
    }

    initAudioContext() {
        try {
            this.audioContext = new (window.AudioContext || window.webkitAudioContext)();
        } catch (error) {
            console.warn('Web Audio API not supported:', error);
        }
    }

    // Call on first user gesture to unlock audio on iOS/Chrome
    resumeOnUserGestureOnce() {
        const resume = () => {
            try {
                if (this.audioContext && this.audioContext.state === 'suspended') {
                    this.audioContext.resume();
                    console.log('Audio context resumed on user gesture');
                }
                // Test sound to ensure it works
                this.playBeepSound();
            } catch (e) {
                console.error('Failed to resume audio context:', e);
            }
            window.removeEventListener('click', resume, true);
            window.removeEventListener('keydown', resume, true);
            window.removeEventListener('touchstart', resume, true);
        };
        window.addEventListener('click', resume, true);
        window.addEventListener('keydown', resume, true);
        window.addEventListener('touchstart', resume, true);
    }

    // Play siren sound for new leads / critical events
    playSirenSound() {
        if (!this.isEnabled || !this.ringtoneEnabled || !this.audioContext) return;

        try {
            // Resume audio context if suspended
            if (this.audioContext.state === 'suspended') {
                this.audioContext.resume();
            }

            // Create siren sound using oscillators
            const oscillator1 = this.audioContext.createOscillator();
            const oscillator2 = this.audioContext.createOscillator();
            const gainNode = this.audioContext.createGain();

            // Configure oscillators for siren effect
            oscillator1.type = 'sine';
            oscillator1.frequency.setValueAtTime(800, this.audioContext.currentTime);
            oscillator1.frequency.exponentialRampToValueAtTime(1200, this.audioContext.currentTime + 0.5);

            oscillator2.type = 'sine';
            oscillator2.frequency.setValueAtTime(1200, this.audioContext.currentTime);
            oscillator2.frequency.exponentialRampToValueAtTime(800, this.audioContext.currentTime + 0.5);

            // Configure gain for volume control
            gainNode.gain.setValueAtTime(0, this.audioContext.currentTime);
            gainNode.gain.linearRampToValueAtTime(0.3, this.audioContext.currentTime + 0.1);
            gainNode.gain.linearRampToValueAtTime(0, this.audioContext.currentTime + 1.5);

            // Connect oscillators to gain node
            oscillator1.connect(gainNode);
            oscillator2.connect(gainNode);
            gainNode.connect(this.audioContext.destination);

            // Play the siren sound
            oscillator1.start(this.audioContext.currentTime);
            oscillator2.start(this.audioContext.currentTime);
            oscillator1.stop(this.audioContext.currentTime + 1.5);
            oscillator2.stop(this.audioContext.currentTime + 1.5);

            // Repeat the siren 3 times
            setTimeout(() => this.playSirenSound(), 2000);
            setTimeout(() => this.playSirenSound(), 4000);

        } catch (error) {
            console.error('Error playing siren sound:', error);
        }
    }

    // Play notification sound for messages
    playNotificationSound() {
        if (!this.isEnabled || !this.audioContext) return;

        try {
            if (this.audioContext.state === 'suspended') {
                this.audioContext.resume();
            }

            const oscillator = this.audioContext.createOscillator();
            const gainNode = this.audioContext.createGain();

            oscillator.type = 'sine';
            oscillator.frequency.setValueAtTime(800, this.audioContext.currentTime);
            oscillator.frequency.exponentialRampToValueAtTime(600, this.audioContext.currentTime + 0.1);

            gainNode.gain.setValueAtTime(0, this.audioContext.currentTime);
            gainNode.gain.linearRampToValueAtTime(0.2, this.audioContext.currentTime + 0.05);
            gainNode.gain.linearRampToValueAtTime(0, this.audioContext.currentTime + 0.3);

            oscillator.connect(gainNode);
            gainNode.connect(this.audioContext.destination);

            oscillator.start(this.audioContext.currentTime);
            oscillator.stop(this.audioContext.currentTime + 0.3);

        } catch (error) {
            console.error('Error playing notification sound:', error);
        }
    }

    // Play a simple beep sound (fallback)
    playBeepSound() {
        if (!this.isEnabled || !this.audioContext) return;

        try {
            if (this.audioContext.state === 'suspended') {
                this.audioContext.resume();
            }

            const oscillator = this.audioContext.createOscillator();
            const gainNode = this.audioContext.createGain();

            oscillator.type = 'sine';
            oscillator.frequency.setValueAtTime(1000, this.audioContext.currentTime);

            gainNode.gain.setValueAtTime(0, this.audioContext.currentTime);
            gainNode.gain.linearRampToValueAtTime(0.3, this.audioContext.currentTime + 0.05);
            gainNode.gain.linearRampToValueAtTime(0, this.audioContext.currentTime + 0.2);

            oscillator.connect(gainNode);
            gainNode.connect(this.audioContext.destination);

            oscillator.start(this.audioContext.currentTime);
            oscillator.stop(this.audioContext.currentTime + 0.2);

        } catch (error) {
            console.error('Error playing beep sound:', error);
        }
    }

    // Enable/disable sounds
    setEnabled(enabled) {
        this.isEnabled = enabled;
        localStorage.setItem('soundNotificationsEnabled', enabled);
    }

    // Check if sounds are enabled
    isSoundEnabled() {
        const stored = localStorage.getItem('soundNotificationsEnabled');
        return stored !== null ? stored === 'true' : true;
    }

    // Initialize from localStorage
    init() {
        this.isEnabled = this.isSoundEnabled();
        const storedR = localStorage.getItem('ringtoneEnabled');
        this.ringtoneEnabled = storedR !== null ? storedR === 'true' : true;
    }

    // Ringtone preference
    setRingtoneEnabled(enabled) {
        this.ringtoneEnabled = enabled;
        localStorage.setItem('ringtoneEnabled', enabled);
    }
    isRingtoneEnabled() {
        const stored = localStorage.getItem('ringtoneEnabled');
        return stored !== null ? stored === 'true' : true;
    }
}

// Create singleton instance
const soundNotificationService = new SoundNotificationService();
soundNotificationService.init();

export default soundNotificationService;
